<template>
  <div class="caisse-wrapper">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Caisse</h1>
        <p>
          Consultez les statistiques de votre caisse et les mouvements
          financiers.
        </p>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-outline"
          @click="fetchStatistics"
          :disabled="statisticsLoading"
        >
          <i
            class="bi bi-arrow-clockwise"
            :class="{ spinning: statisticsLoading }"
          ></i>
          <span>Actualiser</span>
        </button>
      </div>
    </header>

    <section class="page-body fade-in">
      <div v-if="statisticsLoading" class="loading-state">
        <div class="loader"></div>
        <p>Chargement des statistiques de caisse…</p>
      </div>

      <div v-else-if="hasStatistics" class="statistics-grid">
        <div class="stat-card stat-card--balance">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Position de caisse</h3>
              <p class="stat-value">
                {{ formatCurrency(statistics.cashBalance) }}
              </p>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-wallet2"></i>
            </div>
          </div>
          <div class="stat-bottom-section">
            <div class="stat-details-grid">
              <div class="stat-detail-item">
                <span class="detail-label">Reçu des clients</span>
                <span class="detail-value detail-value--positive">{{
                  formatCurrency(statistics.receivedFromCustomers)
                }}</span>
              </div>
              <div class="stat-detail-item">
                <span class="detail-label">Payé au dépôt</span>
                <span class="detail-value detail-value--negative">{{
                  formatCurrency(statistics.paidToDepot)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card--today">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Aujourd'hui</h3>
              <div class="stat-metrics-inline">
                <div class="metric-inline">
                  <span class="metric-label-small">Total</span>
                  <span class="metric-value-inline">{{
                    formatCurrency(statistics.totalPriceToday)
                  }}</span>
                </div>
                <div class="metric-separator"></div>
                <div class="metric-inline">
                  <span class="metric-label-small">Payé</span>
                  <span
                    class="metric-value-inline metric-value-inline--green"
                    >{{ formatCurrency(statistics.amountPaidToday) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-calendar-day"></i>
            </div>
          </div>
          <div class="stat-progress-bar">
            <div
              class="progress-fill"
              :style="{
                width:
                  statistics.totalPriceToday > 0
                    ? (statistics.amountPaidToday /
                        statistics.totalPriceToday) *
                        100 +
                      '%'
                    : '0%',
              }"
            ></div>
          </div>
        </div>

        <div class="stat-card stat-card--month">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Ce mois</h3>
              <div class="stat-metrics-inline">
                <div class="metric-inline">
                  <span class="metric-label-small">Total</span>
                  <span class="metric-value-inline">{{
                    formatCurrency(statistics.totalPriceMonth)
                  }}</span>
                </div>
                <div class="metric-separator"></div>
                <div class="metric-inline">
                  <span class="metric-label-small">Payé</span>
                  <span
                    class="metric-value-inline metric-value-inline--green"
                    >{{ formatCurrency(statistics.amountPaidMonth) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-calendar-month"></i>
            </div>
          </div>
          <div class="stat-progress-bar">
            <div
              class="progress-fill"
              :style="{
                width:
                  statistics.totalPriceMonth > 0
                    ? (statistics.amountPaidMonth /
                        statistics.totalPriceMonth) *
                        100 +
                      '%'
                    : '0%',
              }"
            ></div>
          </div>
        </div>

        <div class="stat-card stat-card--sales">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Ventes totales</h3>
              <p class="stat-value">
                {{ formatCurrency(statistics.salesTotalPrice) }}
              </p>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
          <div class="stat-bottom-section">
            <div class="stat-details-grid">
              <div class="stat-detail-item">
                <span class="detail-label">Payé</span>
                <span class="detail-value detail-value--positive">{{
                  formatCurrency(statistics.salesTotalPaid)
                }}</span>
              </div>
              <div class="stat-detail-item">
                <span class="detail-label">Impayé</span>
                <span class="detail-value detail-value--negative">{{
                  formatCurrency(statistics.salesTotalUnpaid)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="stat-card stat-card--depot">
          <div class="stat-top-section">
            <div class="stat-info">
              <h3 class="stat-label">Chargé du dépôt</h3>
              <p class="stat-value">
                {{ formatCurrency(statistics.totalPriceChargedFromDepot) }}
              </p>
            </div>
            <div class="stat-icon-circle">
              <i class="bi bi-truck"></i>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="bi bi-wallet2"></i>
        <p>Aucune statistique de caisse disponible</p>
        <button class="btn btn-primary" @click="fetchStatistics">
          Actualiser
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "Caisse",
  data() {
    return {
      statisticsLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      statistics: "sellerStatistics/getStatistics",
    }),
    hasStatistics() {
      return this.statistics && Object.keys(this.statistics).length > 0;
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === "")
        return "0,00 MAD";
      const number = Number(amount);
      if (Number.isNaN(number)) return "0,00 MAD";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async fetchStatistics() {
      this.statisticsLoading = true;
      try {
        await this.$store.dispatch("sellerStatistics/getStatistics");
        this.$toast?.success?.("Statistiques actualisées.");
      } catch (error) {
        console.error("Error loading statistics", error);
        this.$toast?.error?.("Impossible de charger les statistiques.");
      } finally {
        this.statisticsLoading = false;
      }
    },
  },
  mounted() {
    this.fetchStatistics();
  },
};
</script>

<style scoped>
.caisse-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
  max-width: 520px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state i {
  font-size: 3rem;
  color: #cbd5e1;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  transition: width 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: transparent;
}

.stat-card:hover::before {
  width: 100%;
  opacity: 0.05;
}

.stat-card--balance::before {
  background: #10b981;
}

.stat-card--today::before {
  background: #3b82f6;
}

.stat-card--month::before {
  background: #9333ea;
}

.stat-card--sales::before {
  background: #f59e0b;
}

.stat-card--depot::before {
  background: #14b8a6;
}

.stat-top-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
}

.stat-info {
  flex: 1;
  min-width: 0;
}

.stat-label {
  margin: 0 0 0.75rem 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.stat-value {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.stat-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.stat-card:hover .stat-icon-circle {
  transform: scale(1.1) rotate(-5deg);
}

.stat-card--balance .stat-icon-circle {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #10b981;
}

.stat-card--today .stat-icon-circle {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #3b82f6;
}

.stat-card--month .stat-icon-circle {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  color: #9333ea;
}

.stat-card--sales .stat-icon-circle {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #f59e0b;
}

.stat-card--depot .stat-icon-circle {
  background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%);
  color: #14b8a6;
}

.stat-bottom-section {
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.stat-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.stat-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.detail-value--positive {
  color: #10b981;
}

.detail-value--negative {
  color: #ef4444;
}

.stat-metrics-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.metric-inline {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-label-small {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metric-value-inline {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.metric-value-inline--green {
  color: #10b981;
}

.metric-separator {
  width: 1px;
  height: 30px;
  background: linear-gradient(
    180deg,
    transparent,
    #e2e8f0 20%,
    #e2e8f0 80%,
    transparent
  );
}

.stat-progress-bar {
  height: 6px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 1rem;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card--today .progress-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
}

.stat-card--month .progress-fill {
  background: linear-gradient(90deg, #9333ea 0%, #a78bfa 100%);
}

.progress-fill::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.spinning {
  animation: spin 1s linear infinite;
}

@media (max-width: 1024px) {
  .caisse-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .header-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .caisse-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .statistics-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .page-body {
    padding: 1.25rem;
  }
  .stat-card {
    padding: 1.5rem;
  }
}
</style>
